import { useEffect, useState, useCallback } from "react";
import api, { errMsg, downloadReport } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Sparkles, Trash2, FileText, FileSpreadsheet } from "lucide-react";

const EMPTY = { category: "symptom", code: "", name: "", description: "", system: "", default_action: "", equipment_type: "", active: true };
const FC_SYSTEMS = ["electrical","engine","transmission","hydraulic","pneumatic","brakes","structural","cooling","fuel","steering","cabin","other"];
const FC_SYS_LABEL = {electrical:"Eléctrico",engine:"Motor",transmission:"Transmisión",hydraulic:"Hidráulico",pneumatic:"Neumático",brakes:"Frenos",structural:"Estructural",cooling:"Refrigeración",fuel:"Combustible",steering:"Dirección",cabin:"Cabina",other:"Otro"};

export default function MntFailureCodes() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const editable = can("admin", "maintenance");

  const load = useCallback(() => {
    api.get("/mnt/failure-codes", { params: filter ? { category: filter } : {} }).then((r) => setRows(r.data));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try { await api.post("/mnt/failure-codes", form); toast.success(t("common.saved")); setOpen(false); setForm(EMPTY); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const seed = async () => {
    if (!window.confirm(t("cls.seedConfirm") || "¿Sembrar catálogo?")) return;
    try {
      const r = await api.post("/mnt/seed-failure-codes");
      toast.success(t("mnt.fc.seedDone").replace("{c}", r.data.created).replace("{s}", r.data.skipped));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar código?")) return;
    try { await api.delete(`/mnt/failure-codes/${id}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="mnt-failure-codes-page">
      <PageHeader title={t("mnt.fc.title")} subtitle={t("mnt.fc.subtitle")}>
        <div className="flex gap-2 flex-wrap">
          <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="rounded-none h-8 text-xs w-40" data-testid="fc-filter"><SelectValue placeholder={t("mnt.fc.category")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">— {t("mnt.fc.category")} —</SelectItem>
              {["symptom","cause","action"].map((c) => <SelectItem key={c} value={c}>{t(`mnt.fc.cat.${c}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          {can("admin") && <Button size="sm" onClick={seed} className="rounded-none text-xs font-bold" data-testid="fc-seed-button"><Sparkles size={13} className="mr-1" /> {t("mnt.fc.seed")}</Button>}
          <div className="flex border">
            <button onClick={async () => { try { await downloadReport(`/reports/mnt/failure-codes?${filter ? `category=${filter}&` : ""}format=csv`, "codigos_falla.csv"); } catch (e) { toast.error(errMsg(e)); } }}
              className="border-r px-2 h-8 text-[10px] font-bold hover:bg-muted flex items-center gap-1" data-testid="fc-export-csv"><FileText size={12} /> CSV</button>
            <button onClick={async () => { try { await downloadReport(`/reports/mnt/failure-codes?${filter ? `category=${filter}&` : ""}format=xlsx`, "codigos_falla.xlsx"); } catch (e) { toast.error(errMsg(e)); } }}
              className="px-2 h-8 text-[10px] font-bold hover:bg-muted flex items-center gap-1" data-testid="fc-export-xlsx"><FileSpreadsheet size={12} /> XLSX</button>
          </div>
          {editable && (
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(EMPTY); }}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-none text-xs font-bold" data-testid="fc-new-button"><Plus size={13} className="mr-1" /> {t("mnt.fc.new")}</Button>
              </DialogTrigger>
              <DialogContent className="rounded-none sm:max-w-md">
                <DialogHeader><DialogTitle className="uppercase font-black">{t("mnt.fc.new")}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.fc.category")}</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="rounded-none h-8 text-xs" data-testid="fc-form-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{["symptom","cause","action"].map((c) => <SelectItem key={c} value={c}>{t(`mnt.fc.cat.${c}`)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.m.code")}</Label><Input value={form.code} maxLength={4} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-none h-8 text-xs font-black tabular-nums" data-testid="fc-form-code" /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-[10px] font-bold uppercase">{t("cls.name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none h-8 text-xs" data-testid="fc-form-name" /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-[10px] font-bold uppercase">{t("mnt.description")}</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none h-8 text-xs" data-testid="fc-form-description" /></div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase">Sistema</Label>
                    <select value={form.system} onChange={(e) => setForm({ ...form, system: e.target.value })}
                      className="w-full border h-8 text-xs bg-background" data-testid="fc-form-system">
                      <option value="">—</option>
                      {FC_SYSTEMS.map(s => <option key={s} value={s}>{FC_SYS_LABEL[s]}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1"><Label className="text-[10px] font-bold uppercase">Tipo de equipo</Label>
                    <Input value={form.equipment_type} onChange={(e) => setForm({ ...form, equipment_type: e.target.value })}
                      className="rounded-none h-8 text-xs" placeholder="Camión / Pala / Perforadora…" data-testid="fc-form-eqtype" /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-[10px] font-bold uppercase">Acción sugerida</Label>
                    <Input value={form.default_action} onChange={(e) => setForm({ ...form, default_action: e.target.value })}
                      className="rounded-none h-8 text-xs" placeholder="Cambio de rodamiento / Ajuste de válvulas / Reemplazo de sensor…" data-testid="fc-form-action" /></div>
                </div>
                <DialogFooter><Button onClick={save} disabled={!form.code || !form.name} className="rounded-none text-xs font-bold" data-testid="fc-save-button">{t("common.save")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </PageHeader>

      {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="fc-table"><table className="tbl">
          <thead><tr><th>{t("mnt.fc.category")}</th><th>{t("mnt.m.code")}</th><th>{t("cls.name")}</th><th>{t("mnt.description")}</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`fc-row-${r.category}-${r.code}`}>
                <td className="text-[10px] font-bold uppercase">{t(`mnt.fc.cat.${r.category}`)}</td>
                <td className="font-bold tabular-nums">{r.code}</td>
                <td>{r.name}</td>
                <td className="text-[10px] text-muted-foreground">{r.description || "—"}</td>
                <td>{can("admin") && <button onClick={() => del(r.id)} className="text-muted-foreground hover:text-red-600" data-testid={`fc-del-${r.category}-${r.code}`}><Trash2 size={11} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}
